// src/services/negociosService.js

import {
  collection,
  doc,
  addDoc,
  setDoc,
  onSnapshot,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
  startAfter,
  increment,
} from 'firebase/firestore';

import { db } from './firebase';

// ✅ Score Events
import { EVENT_TYPES } from './leadershipEngine/score.config';

// ✅ Sócios (para montar allowedUids = [eu + sócios aceitos])
import { getSociosAceitosIds } from './sociosService';

/* ============================================================
   HELPERS
============================================================ */

function parseNumero(val) {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'number') return val;

  const digits = String(val).replace(/[^\d]/g, '');
  return digits ? Number(digits) : null;
}

function slugify(str = '') {
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function normalizarBairros(dados) {
  if (Array.isArray(dados.bairros) && dados.bairros.length > 0) {
    return dados.bairros.map((b) => String(b).trim()).filter(Boolean);
  }
  if (Array.isArray(dados.bairrosDesejados) && dados.bairrosDesejados.length) {
    return dados.bairrosDesejados.map((b) => String(b).trim()).filter(Boolean);
  }
  if (typeof dados.bairros === 'string') {
    return dados.bairros
      .split(',')
      .map((b) => b.trim())
      .filter(Boolean);
  }
  return [];
}

function uniq(arr = []) {
  return Array.from(new Set((arr || []).filter(Boolean)));
}

// ✅ NOVO: normaliza título (garante uppercase no banco)
function normalizeTituloNegocio(v) {
  const s = String(v || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();
  return s || '';
}

/* ============================================================
   ✅ HELPERS NOVOS — Enriquecimento do Negócio (Tags / Perfil)
============================================================ */

// Faixa -> percentual “alvo” (aprox) p/ lógica
function faixaToPercent(faixa) {
  if (faixa === '30-50') return 40;
  if (faixa === '50-90') return 70;
  return null; // negociável ou vazio
}

function sumPermutaValorTotal(permuta) {
  if (!permuta?.aceita) return 0;
  const itens = Array.isArray(permuta.itens) ? permuta.itens : [];
  return itens.reduce((acc, it) => acc + (Number(it?.valorAproximado) || 0), 0);
}

// Detecta movimento com base no que você JÁ salva hoje (origem/ids)
function detectMovimentoFromDados(dados = {}) {
  const origem = String(dados.origem || '').toLowerCase();

  // 🔥 chave: Downgrade só existe se você salvar algo (origem/downgradeImovelId)
  if (origem.includes('downgrade')) return 'downgrade';
  if (origem.includes('upgrade')) return 'upgrade';

  // fallback por IDs (se você passar)
  if (dados.downgradeImovelId) return 'downgrade';
  if (dados.upgradeImovelId) return 'upgrade';

  return 'padrao';
}

// Perfil tático: sem “psicologia” no código — só operação de fechamento
function computePerfilTatico({
  movimento,
  permutaFaixa,
  entrada,
  permutaValorTotal,
}) {
  const pct = faixaToPercent(permutaFaixa);
  const entradaNum = Number(entrada || 0);
  const poder = entradaNum + (Number(permutaValorTotal) || 0);

  const permutaAlta = pct !== null ? pct >= 70 : false;

  const entradaAlta = entradaNum >= 200000; // ajuste depois (regra do produto)
  const permutaBaixa = pct !== null ? pct <= 40 : false;

  if (permutaBaixa && entradaAlta) return 'SNIPER';

  if (permutaAlta && movimento === 'padrao') return 'TRADER';

  if (permutaAlta && movimento === 'upgrade') return 'CONSOLIDADOR';
  if (permutaAlta && movimento === 'downgrade') return 'PULVERIZADOR';

  if ((pct === 40 || pct === 70 || pct === null) && movimento === 'upgrade') {
    return 'ALAVANCADOR';
  }

  return poder > 0 ? 'PADRAO' : 'PADRAO';
}

// Tags para query e UI
function buildTagsDeMatch({
  movimento,
  permuta,
  entrada,
  permutaValorTotal,
  perfilTatico,
  tipoImovel,
}) {
  const tags = [];

  if (movimento === 'upgrade') tags.push('MOV_UPGRADE');
  if (movimento === 'downgrade') tags.push('MOV_DOWNGRADE');
  if (movimento === 'padrao') tags.push('MOV_PADRAO');

  if (perfilTatico) tags.push(`PERFIL_${perfilTatico}`);

  if (permuta?.aceita) tags.push('ACEITA_PERMUTA');

  const pct = faixaToPercent(permuta?.faixa);
  if (pct !== null) {
    if (pct <= 40) tags.push('PERMUTA_BAIXA');
    if (pct > 40 && pct < 70) tags.push('PERMUTA_MEDIA');
    if (pct >= 70) tags.push('PERMUTA_ALTA');
  } else if (permuta?.aceita && permuta?.faixa === 'negociavel') {
    tags.push('PERMUTA_NEGOCIAVEL');
  }

  const entradaNum = Number(entrada || 0);
  if (entradaNum > 0) tags.push('TEM_ENTRADA');

  const permTotal = Number(permutaValorTotal || 0);
  if (permTotal > 0) tags.push('TEM_PERMUTA_VALOR');

  const poderTotal = entradaNum + permTotal;
  if (poderTotal >= 500000) tags.push('PODER_ALTO');
  else if (poderTotal >= 150000) tags.push('PODER_MEDIO');
  else if (poderTotal > 0) tags.push('PODER_BAIXO');

  if (tipoImovel) tags.push(`QUER_${slugify(tipoImovel).toUpperCase()}`);

  return uniq(tags);
}

// Função principal de enriquecimento
function enrichNegocioDados(base = {}) {
  const permuta = base.permuta || null;
  const entrada = base.entrada_disponivel ?? null;

  const movimento = detectMovimentoFromDados(base);
  const permutaValorTotal = sumPermutaValorTotal(permuta);
  const permutaPercentualAlvo = faixaToPercent(permuta?.faixa || '');
  const poderTotal =
    (Number(entrada || 0) || 0) + (Number(permutaValorTotal) || 0);

  const perfilTatico = computePerfilTatico({
    movimento,
    permutaFaixa: permuta?.faixa || '',
    entrada,
    permutaValorTotal,
  });

  const tagsDeMatch = buildTagsDeMatch({
    movimento,
    permuta,
    entrada,
    permutaValorTotal,
    perfilTatico,
    tipoImovel: base.tipoImovel || base.tipo || '',
  });

  return {
    movimento,
    permutaValorTotal,
    permutaPercentualAlvo,
    poderTotal,
    perfilTatico,
    tagsDeMatch,
  };
}

/**
 * ✅ Helper de Score Event
 */
async function registrarEventoScore({ uid, tipo, refId = null }) {
  try {
    if (!uid || !tipo) return;
    await addDoc(collection(db, 'eventos_score'), {
      uid,
      tipo,
      refId: refId || null,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn(
      '[ScoreEvent] Falha ao registrar evento:',
      tipo,
      err?.message || err
    );
  }
}

/**
 * ✅ Monta allowedUids (apenas: eu + sócios aceitos)
 * OBS: não entra parceiros (regra do produto).
 */
async function buildAllowedUids(uid) {
  const socios = await getSociosAceitosIds(uid);
  return uniq([uid, ...(socios || [])]);
}

/* ============================================================
   CREATE (Híbrido – robusto + compatível)
============================================================ */

export async function createNegocio(dadosPublicos, dadosPrivados) {
  try {
    // ✅ ENRIQUECE AQUI TAMBÉM (garante consistência mesmo se chamarem direto)
    const enriched = enrichNegocioDados(dadosPublicos);

    const payloadPublico = {
      ...dadosPublicos,

      // ✅ NOVO: normaliza título no banco
      tituloNegocio: normalizeTituloNegocio(dadosPublicos.tituloNegocio),

      statusPipeline: dadosPublicos.statusPipeline || 'contato',
      origem: dadosPublicos.origem || 'manual',

      // ✅ já existia
      upgradeImovelId: dadosPublicos.upgradeImovelId || null,

      // ✅ NOVO: downgrade (se existir)
      downgradeImovelId: dadosPublicos.downgradeImovelId || null,

      // ✅ NOVO: campos ocultos (engine)
      ...enriched,

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const negocioRef = await addDoc(collection(db, 'negocios'), payloadPublico);

    await registrarEventoScore({
      uid: payloadPublico.corretorId,
      tipo: EVENT_TYPES.NEGOCIO_CADASTRADO,
      refId: negocioRef.id,
    });

    if (dadosPrivados) {
      try {
        await setDoc(doc(db, 'negocios', negocioRef.id, 'privado', 'dados'), {
          ...dadosPrivados,
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        console.error('Aviso: erro ao salvar dados privados (ignorado):', err);
      }
    }

    return negocioRef.id;
  } catch (error) {
    console.error('Erro ao criar negócio:', error);
    throw error;
  }
}

/* ============================================================
   ✅ Registrar "Visita Realizada" dentro do Negócio
============================================================ */

export async function registrarVisitaRealizadaNoNegocio(negocioId, corretorId) {
  if (!negocioId) throw new Error('negocioId é obrigatório.');
  if (!corretorId) throw new Error('corretorId é obrigatório.');

  try {
    await updateDoc(doc(db, 'negocios', negocioId), {
      visitasRealizadasCount: increment(1),
      ultimaVisitaRealizadaEm: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn(
      '[Negocio] Falha ao atualizar contador/última visita:',
      err?.message || err
    );
  }

  await registrarEventoScore({
    uid: corretorId,
    tipo: EVENT_TYPES.VISITA_REALIZADA,
    refId: negocioId,
  });

  return { ok: true };
}

/* ============================================================
   criarNegocio (Wrapper de compatibilidade)
============================================================ */

export async function criarNegocio({ usuario, dados }) {
  if (!usuario) throw new Error('Usuário não autenticado.');

  const dadosPrivados = {
    telefone: dados.telefone || '',
    whatsapp: dados.whatsapp || '',
    email: dados.email || '',
  };

  const statusPipeline =
    dados.statusPipeline || dados.status_pipeline || 'contato';
  const origem = dados.origem || 'manual';

  const upgradeImovelId =
    dados.upgradeImovelId || dados.upgrade_imovel_id || null;

  // ✅ NOVO: downgrade (se vier do Cadastrar / futuro)
  const downgradeImovelId =
    dados.downgradeImovelId || dados.downgrade_imovel_id || null;

  const bairrosArr = normalizarBairros(dados);

  const nomeCliente =
    dados.nomeCliente || dados.clientePrimeiroNome || dados.nome || '';
  const clientePrimeiroNome = nomeCliente.split(' ')[0] || nomeCliente;

  const alvo_id_imovel = dados.alvo_id_imovel || dados.alvoIdImovel || null;

  const itens_disponiveis_ids = Array.isArray(dados.itens_disponiveis_ids)
    ? dados.itens_disponiveis_ids
    : [];

  const entrada_disponivel =
    dados.entrada_disponivel === null || dados.entrada_disponivel === undefined
      ? null
      : Number(dados.entrada_disponivel || 0);

  const observacoes_corretor = dados.observacoes_corretor || '';

  let allowedUids = [];
  try {
    allowedUids = await buildAllowedUids(usuario.uid);
  } catch (e) {
    allowedUids = [usuario.uid];
  }

  const corretorNome =
    usuario.nome || usuario.displayName || usuario.nomeCompleto || 'Corretor';

  // ✅ NOVO: título
  const tituloNegocio = normalizeTituloNegocio(dados.tituloNegocio);

  const dadosPublicos = {
    ...dados,

    // ✅ NOVO: persistência
    tituloNegocio,

    corretorId: usuario.uid,
    corretorNome,

    ownerId: usuario.uid,
    ownerNome: corretorNome,

    allowedUids,

    origem,
    statusPipeline,
    upgradeImovelId,
    downgradeImovelId,

    nomeCliente,
    clientePrimeiroNome,

    tipoImovel: dados.tipoImovel || dados.tipo || '',
    cidadePrincipal: dados.cidade || dados.cidadePrincipal || '',

    bairros: bairrosArr,
    bairrosDesejados: bairrosArr,
    bairrosSlug: bairrosArr.map((b) => slugify(b)),

    valorMin: parseNumero(dados.valorMin),
    valorMax: parseNumero(dados.valorMax),

    buscaTags: dados.buscaTags || (dados.objetivo ? [dados.objetivo] : []),

    visibilidade: dados.visibilidade || 'privado',

    alvo_id_imovel,
    itens_disponiveis_ids,
    entrada_disponivel,
    observacoes_corretor,
  };

  return createNegocio(dadosPublicos, dadosPrivados);
}

/* ============================================================
   LISTENS
============================================================ */

export function listenNegociosVisiveis(uid, callback) {
  const q = query(
    collection(db, 'negocios'),
    where('allowedUids', 'array-contains', uid),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snap) => {
    const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(lista);
  });
}

export function listenMeusNegocios(uid, callback) {
  const q = query(
    collection(db, 'negocios'),
    where('corretorId', '==', uid),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snap) => {
    const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(lista);
  });
}

export function listenNegociosComunidade(uid, callback) {
  const q = query(
    collection(db, 'negocios'),
    where('visibilidade', '==', 'comunidade'),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snap) => {
    const lista = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((d) => d.corretorId !== uid);
    callback(lista);
  });
}

/* ============================================================
   GET (visíveis) — ✅ NOVO p/ Máquina de Vendas (getDocs)
============================================================ */

export async function getNegociosVisiveis(uid) {
  const q = query(
    collection(db, 'negocios'),
    where('allowedUids', 'array-contains', uid),
    orderBy('createdAt', 'desc')
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/* ============================================================
   GET PRIVADO
============================================================ */

export async function getNegocioPrivado(negocioId) {
  const ref = doc(db, 'negocios', negocioId, 'privado', 'dados');
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

/* ============================================================
   ✅ MATCHES DO SÓCIO (cache do backend)
   Path:
   negocios/{negocioId}/matchesByOwner/{uid}/items/{matchId}
============================================================ */

export async function listarMyMatchesForNegocio(negocioId, uid, max = 50) {
  if (!negocioId) throw new Error('negocioId é obrigatório.');
  if (!uid) throw new Error('uid é obrigatório.');

  const q = query(
    collection(db, 'negocios', negocioId, 'matchesByOwner', uid, 'items'),
    orderBy('score_match', 'desc'),
    limit(max)
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export function listenMyMatchesForNegocio(negocioId, uid, callback, max = 50) {
  if (!negocioId || !uid) return () => {};

  const q = query(
    collection(db, 'negocios', negocioId, 'matchesByOwner', uid, 'items'),
    orderBy('score_match', 'desc'),
    limit(max)
  );

  return onSnapshot(
    q,
    (snap) => {
      const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(Array.isArray(lista) ? lista : []);
    },
    (err) => {
      console.error('[listenMyMatchesForNegocio] erro:', err);
      callback([]);
    }
  );
}

/* ============================================================
   ✅ RESPOSTA DO SÓCIO AO NEGÓCIO
   Path:
   negocios/{negocioId}/respostas/{uid}
============================================================ */

export async function responderNegocio(
  negocioId,
  uid,
  { imoveisSelecionados = [], message = '' } = {}
) {
  if (!negocioId) throw new Error('negocioId é obrigatório.');
  if (!uid) throw new Error('uid é obrigatório.');

  const ref = doc(db, 'negocios', negocioId, 'respostas', uid);

  const payload = {
    uid,
    negocioId,
    imoveisSelecionados: Array.isArray(imoveisSelecionados)
      ? uniq(imoveisSelecionados).slice(0, 50)
      : [],
    message: String(message || '').trim(),
    sentAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(ref, payload, { merge: true });
  return { ok: true };
}

/* ============================================================
   ✅ NOTIFICAÇÕES: marcar como lida
   Path:
   notificacoes/{uid}/items/{notifId}
============================================================ */

export async function markNotificacaoAsRead(uid, notifId) {
  if (!uid) throw new Error('uid é obrigatório.');
  if (!notifId) throw new Error('notifId é obrigatório.');

  const ref = doc(db, 'notificacoes', uid, 'items', notifId);
  await updateDoc(ref, {
    read: true,
    updatedAt: serverTimestamp(),
  });

  return { ok: true };
}

/* ============================================================
   PAGINAÇÃO
============================================================ */

export async function getMeusNegocios(uid, pageSize = 20, lastDocSnap = null) {
  try {
    const baseQuery = query(
      collection(db, 'negocios'),
      where('corretorId', '==', uid),
      orderBy('updatedAt', 'desc'),
      limit(pageSize)
    );

    const q = lastDocSnap
      ? query(baseQuery, startAfter(lastDocSnap))
      : baseQuery;

    const results = await getDocs(q);

    return {
      negocios: results.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })),
      lastDoc: results.docs.length
        ? results.docs[results.docs.length - 1]
        : null,
    };
  } catch (error) {
    console.error('Erro ao listar meus negócios:', error);
    throw error;
  }
}

export async function getNegociosComunidade(
  uid,
  pageSize = 20,
  lastDocSnap = null
) {
  try {
    const baseQuery = query(
      collection(db, 'negocios'),
      where('visibilidade', '==', 'comunidade'),
      orderBy('updatedAt', 'desc'),
      limit(pageSize)
    );

    const q = lastDocSnap
      ? query(baseQuery, startAfter(lastDocSnap))
      : baseQuery;

    const results = await getDocs(q);

    return {
      negocios: results.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter((d) => d.corretorId !== uid),
      lastDoc: results.docs.length
        ? results.docs[results.docs.length - 1]
        : null,
    };
  } catch (error) {
    console.error('Erro ao listar negócios da comunidade:', error);
    throw error;
  }
}

/* ============================================================
   RESTO
============================================================ */

export async function getNegocioById(negocioId) {
  const snap = await getDoc(doc(db, 'negocios', negocioId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * ✅ Atualizar negócio
 * - agora também recalcula tags/perfil quando mexer em permuta/entrada/origem/tipo
 */
export async function atualizarNegocio(negocioId, dados) {
  try {
    const bairrosArr = normalizarBairros(dados);

    const payload = {
      ...dados,
      updatedAt: serverTimestamp(),
    };

    // ✅ NOVO: se vier título, normaliza
    if (dados.tituloNegocio !== undefined) {
      payload.tituloNegocio = normalizeTituloNegocio(dados.tituloNegocio);
    }

    if (bairrosArr.length) {
      payload.bairros = bairrosArr;
      payload.bairrosDesejados = bairrosArr;
      payload.bairrosSlug = bairrosArr.map((b) => slugify(b));
    }

    if (dados.valorMin !== undefined)
      payload.valorMin = parseNumero(dados.valorMin);
    if (dados.valorMax !== undefined)
      payload.valorMax = parseNumero(dados.valorMax);

    if (
      dados.alvo_id_imovel !== undefined ||
      dados.alvoIdImovel !== undefined
    ) {
      payload.alvo_id_imovel =
        dados.alvo_id_imovel || dados.alvoIdImovel || null;
    }

    if (dados.itens_disponiveis_ids !== undefined) {
      payload.itens_disponiveis_ids = Array.isArray(dados.itens_disponiveis_ids)
        ? dados.itens_disponiveis_ids
        : [];
    }

    if (dados.entrada_disponivel !== undefined) {
      payload.entrada_disponivel =
        dados.entrada_disponivel === null ||
        dados.entrada_disponivel === undefined
          ? null
          : Number(dados.entrada_disponivel || 0);
    }

    if (dados.observacoes_corretor !== undefined) {
      payload.observacoes_corretor = String(dados.observacoes_corretor || '');
    }

    if (dados.allowedUids !== undefined) {
      payload.allowedUids = uniq(dados.allowedUids);
    }

    // ✅ REGRAS: se mudarem campos que afetam engine, recalcula enriquecimento
    const mexeEngine =
      dados.origem !== undefined ||
      dados.tipoImovel !== undefined ||
      dados.tipo !== undefined ||
      dados.permuta !== undefined ||
      dados.entrada_disponivel !== undefined ||
      dados.upgradeImovelId !== undefined ||
      dados.downgradeImovelId !== undefined;

    if (mexeEngine) {
      const base = { ...dados };
      const enriched = enrichNegocioDados(base);

      payload.movimento = enriched.movimento;
      payload.permutaValorTotal = enriched.permutaValorTotal;
      payload.permutaPercentualAlvo = enriched.permutaPercentualAlvo;
      payload.poderTotal = enriched.poderTotal;
      payload.perfilTatico = enriched.perfilTatico;
      payload.tagsDeMatch = enriched.tagsDeMatch;
    }

    await updateDoc(doc(db, 'negocios', negocioId), payload);
  } catch (error) {
    console.error('Erro ao atualizar negócio:', error);
    throw error;
  }
}

export async function deleteNegocio(negocioId) {
  try {
    await deleteDoc(doc(db, 'negocios', negocioId));
  } catch (error) {
    console.error('Erro ao deletar negócio:', error);
    throw error;
  }
}
